// DiffBoard — modern diff viewer.
//
// Layout:
//   [ toolbar — brand · before ⇄ after chips · stats badges · mode toggle · exit ]
//   [ DiffNav (unified WorkspaceNav) | board(s) | DiffDetailsPanel                ]

import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import { IconButton, SegmentedControl } from "@archlang/ui";
import type { ChangeSet, EntityRef, ModuleDiff, Status } from "@archlang/engine";
import { isMarkdownPath } from "@archlang/viewer-core";
import { useDiff, type DiffState } from "./useDiff.js";
import { SplitDiffBoard } from "./SplitDiffBoard.js";
import { OverlayDiffBoard } from "./OverlayDiffBoard.js";
import { ProcessDiffView } from "./ProcessDiffView.js";
import { MarkdownDiffView } from "./MarkdownDiffView.js";
import { DiffNav } from "../workbench/nav/DiffNav.js";
import { DiffDetailsPanel } from "../components/diff/DiffDetailsPanel.js";
import { SourceMenu, type SourceSlot } from "../components/SourceMenu.js";
import { SourceDropTarget } from "../components/SourceDropTarget.js";
import type { LoadedSources } from "../sourceLoader.js";
import { useDiffMode, setDiffChangeSet, setDiffMode } from "../workbench/diffModeStore.js";

type Mode = "split" | "overlay" | "processes" | "documents";

/** Count markdown entries that differ between the two VFS bundles. */
function docChangeCount(before: ReadonlyMap<string, string>, after: ReadonlyMap<string, string>): number {
  const paths = new Set<string>();
  for (const p of before.keys()) if (isMarkdownPath(p)) paths.add(p);
  for (const p of after.keys()) if (isMarkdownPath(p)) paths.add(p);
  let n = 0;
  for (const p of paths) if ((before.get(p) ?? null) !== (after.get(p) ?? null)) n++;
  return n;
}

export interface DiffBoardProps {
  readonly before: SourceSlot;
  readonly after: SourceSlot;
  readonly currentSources: SourceSlot;
  readonly compareAgainst: SourceSlot;
  readonly compareSwapped: boolean;
  readonly onSetCompare: (slot: SourceSlot | null) => void;
  readonly onSwap: () => void;
  readonly onClear: () => void;
  readonly onLoadIntoWorkspace: (slot: SourceSlot) => void;
  /**
   * When true: no toolbar (SourceMenu / before‑after chips / exit),
   * no internal DiffNav column (outer WorkspaceNav serves that role),
   * and mode is read from / written to diffModeStore so the Studio
   * TopBar can render the mode tab strip instead.
   */
  readonly studioMode?: boolean;
  /**
   * Pre-computed diff from a server-side diff endpoint. When provided,
   * the expensive client-side useDiff pipeline is skipped and this data
   * is used instead. Required when studioMode needs the correct diff
   * (e.g. for proposed changesets where before ≠ after VFS).
   */
  readonly precomputedDiff?: DiffState;
}

interface ChangeStats {
  added: number;
  removed: number;
  modified: number;
  renamed: number;
}

function changeStats(cs: ChangeSet): ChangeStats {
  const out: ChangeStats = { added: 0, removed: 0, modified: 0, renamed: 0 };
  const bump = (s: Status) => {
    if (s === "added") out.added++;
    else if (s === "removed") out.removed++;
    else if (s === "modified") out.modified++;
    else if (s === "renamed") out.renamed++;
  };
  const walk = (m: ModuleDiff) => {
    bump(m.status);
    for (const i of m.interfaces.values()) bump(i.status);
    for (const c of m.children.values()) walk(c);
  };
  for (const m of cs.modules.values()) walk(m);
  for (const p of cs.processes.values()) bump(p.status);
  for (const sp of cs.subprocesses.values()) bump(sp.status);
  for (const v of cs.views.values()) bump(v.status);
  return out;
}

// Toolbar/layout chrome — Tailwind utilities (migrated from the old .diff-* CSS
// in index.css; colours resolve through the var-backed slate/sky/success/…
// theme tokens so they re-theme with data-theme exactly as before).
const CHIP =
  "inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full text-xs max-w-[28ch] min-w-0 bg-slate-400/[0.08] border border-slate-400/15";
const STAT_BASE =
  "inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-xs font-semibold font-mono border";
const STAT_VARIANT: Partial<Record<Status, string>> = {
  added: "bg-success/[0.14] text-success border-success/30",
  modified: "bg-warning/[0.14] text-warning border-warning/30",
  renamed: "bg-sky-400/[0.14] text-sky-300 border-sky-400/30",
  removed: "bg-danger/[0.14] text-danger border-danger/30",
};

export function DiffBoard({
  before,
  after,
  currentSources,
  compareAgainst,
  compareSwapped,
  onSetCompare,
  onSwap,
  onClear,
  onLoadIntoWorkspace,
  studioMode,
  precomputedDiff,
}: DiffBoardProps) {
  // In studioMode the mode tabs live in the Studio TopBar; read from the store.
  const storeMode = useDiffMode();
  const [internalMode, setInternalModeState] = useState<Mode>("split");
  const mode: Mode = studioMode ? storeMode : internalMode;
  const setMode = (m: Mode) => {
    if (studioMode) { setDiffMode(m); }
    else { setInternalModeState(m); }
  };

  const [selectedRef, setSelectedRef] = useState<EntityRef | null>(null);
  // The board highlights modules only; derive a module id from the selection.
  const selectedId =
    selectedRef?.kind === "module"
      ? selectedRef.id
      : selectedRef?.kind === "interface"
        ? selectedRef.moduleId
        : null;
  const selectModule = (id: string | null) =>
    setSelectedRef(id ? ({ kind: "module", id } as EntityRef) : null);

  // Always call useDiff (hooks rule). When precomputedDiff is provided, pass
  // null to skip the expensive model pipeline — document diff uses before.vfs /
  // after.vfs directly (docChangeCount + MarkdownDiffView), which still work.
  const clientDiff = useDiff(
    precomputedDiff ? null : before.vfs,
    precomputedDiff ? null : after.vfs,
  );
  const {
    changeSet,
    before: beforeMods,
    after: afterMods,
    beforeEdges,
    afterEdges,
    beforeRaw,
    afterRaw,
    loading,
    error,
  } = precomputedDiff ?? clientDiff;

  // In studioMode, sync the changeSet into the shared store so WorkspaceNav
  // and the Studio TopBar can read it without prop-drilling.
  useEffect(() => {
    if (!studioMode) return;
    setDiffChangeSet(changeSet);
    return () => { setDiffChangeSet(null); };
  }, [studioMode, changeSet]);

  const stats = useMemo(() => (changeSet ? changeStats(changeSet) : null), [changeSet]);
  const totalChanges = stats
    ? stats.added + stats.removed + stats.modified + stats.renamed
    : 0;
  const docCount = useMemo(() => docChangeCount(before.vfs, after.vfs), [before.vfs, after.vfs]);

  const handleDrop = (loaded: LoadedSources) => {
    onLoadIntoWorkspace({ vfs: loaded.vfs, label: loaded.label });
  };

  const menuProps = {
    currentLabel: currentSources.label,
    currentSources,
    onLoaded: (l: LoadedSources) =>
      onLoadIntoWorkspace({ vfs: l.vfs, label: l.label }),
    onResetDemo: () => onClear(),
    compareAgainst,
    compareSwapped,
    onSetCompare,
    onSwapCompare: onSwap,
    onClearCompare: onClear,
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-900 text-slate-200">
      {!studioMode && <SourceDropTarget onLoaded={handleDrop} />}
      {!studioMode && (
        <div className="flex items-center gap-2.5 px-[14px] py-2 min-h-[44px] border-b border-slate-800 bg-slate-900/[0.94] backdrop-blur-[6px]">
          <div className="flex items-center gap-1.5 pr-1 mr-1 border-r border-slate-800">
            <SourceMenu {...menuProps} />
          </div>

          <div className="flex items-center gap-1.5 shrink min-w-0">
            <span className={CHIP} title={before.label}>
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 shrink-0">before</span>
              <span className="font-mono text-slate-300 overflow-hidden text-ellipsis whitespace-nowrap min-w-0">{before.label}</span>
            </span>
            <IconButton
              size="sm"
              aria-label="Swap before ↔ after"
              title="Swap before ↔ after"
              className="shrink-0"
              onClick={onSwap}
            >
              <ArrowLeftRight width={14} height={14} strokeWidth={1.5} aria-hidden="true" />
            </IconButton>
            <span className={CHIP} title={after.label}>
              <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 shrink-0">after</span>
              <span className="font-mono text-slate-300 overflow-hidden text-ellipsis whitespace-nowrap min-w-0">{after.label}</span>
            </span>
          </div>

          {stats && (
            <div className="flex items-center gap-1">
              <StatBadge label="added" count={stats.added} kind="added" />
              <StatBadge label="modified" count={stats.modified} kind="modified" />
              <StatBadge label="renamed" count={stats.renamed} kind="renamed" />
              <StatBadge label="removed" count={stats.removed} kind="removed" />
              {docCount > 0 && (
                <span className={`${STAT_BASE} bg-sky-400/[0.14] text-sky-300 border-sky-400/30`} title={`${docCount} document${docCount === 1 ? "" : "s"}`}>
                  <span className="text-[13px] leading-none">🖹</span>
                  <span>{docCount}</span>
                </span>
              )}
              {totalChanges === 0 && docCount === 0 && (
                <span className="text-xs text-slate-400 italic px-1.5">no changes</span>
              )}
            </div>
          )}

          <div className="flex-1" />

          <div className="flex items-center gap-1.5">
            <SegmentedControl
              label="Diff view mode"
              active={mode}
              onSelect={setMode}
              items={[
                { id: "split", label: "Split" },
                { id: "overlay", label: "Overlay" },
                { id: "processes", label: "Processes" },
                { id: "documents", label: "Documents" },
              ]}
            />
          </div>

          <IconButton
            size="sm"
            aria-label="Exit diff (return to viewer)"
            title="Exit diff (return to viewer)"
            className="shrink-0 text-sm hover:bg-danger/15 hover:text-danger"
            onClick={onClear}
          >
            ✕
          </IconButton>
        </div>
      )}

      {error && (
        <div className="px-[18px] py-[14px] text-[13px] border-b border-slate-800 text-danger bg-danger/[0.08]">
          {error}
        </div>
      )}
      {loading && (
        <div className="px-[18px] py-[14px] text-[13px] border-b border-slate-800 text-slate-400 italic">
          Computing diff…
        </div>
      )}
      {beforeMods && afterMods && changeSet && (
        <div className={`flex-1 grid ${studioMode ? "grid-cols-[1fr_320px]" : "grid-cols-[280px_1fr_320px]"} min-h-0`}>
          {!studioMode && (
            <aside className="flex flex-col border-r border-slate-800 bg-slate-900/(--tint-strong) min-h-0">
              <DiffNav changeSet={changeSet} selected={selectedRef} onFocus={setSelectedRef} />
            </aside>
          )}
          <div className="relative min-w-0 min-h-0">
            {mode === "split" && (
              <SplitDiffBoard
                before={beforeMods}
                after={afterMods}
                beforeEdges={beforeEdges}
                afterEdges={afterEdges}
                changeSet={changeSet}
                selectedId={selectedId}
                onSelect={selectModule}
              />
            )}
            {mode === "overlay" && (
              <OverlayDiffBoard
                before={beforeMods}
                after={afterMods}
                beforeEdges={beforeEdges}
                afterEdges={afterEdges}
                changeSet={changeSet}
                selectedId={selectedId}
                onSelect={selectModule}
              />
            )}
            {mode === "processes" && beforeRaw && afterRaw && (
              <ProcessDiffView changeSet={changeSet} beforeRaw={beforeRaw} afterRaw={afterRaw} />
            )}
            {mode === "documents" && (
              <MarkdownDiffView before={before.vfs} after={after.vfs} />
            )}
          </div>
          <DiffDetailsPanel
            selected={selectedRef}
            before={beforeMods}
            after={afterMods}
            changeSet={changeSet}
          />
        </div>
      )}
    </div>
  );
}

function StatBadge({
  label,
  count,
  kind,
}: {
  readonly label: string;
  readonly count: number;
  readonly kind: Status;
}) {
  if (count === 0) return null;
  const sigil = kind === "added" ? "+" : kind === "removed" ? "−" : kind === "modified" ? "~" : "↻";
  return (
    <span className={`${STAT_BASE} ${STAT_VARIANT[kind] ?? ""}`} title={`${count} ${label}`}>
      <span className="text-[13px] leading-none">{sigil}</span>
      <span>{count}</span>
    </span>
  );
}
